import {
  AddStageType,
  AvaliacaoEtapaType,
  GradeJustifyPayload,
  NewAddStudentGrade,
  NewRemoveStudentGrade,
  NewStudentGrade,
  NewStudentGradeParams,
  NotaType,
  Res,
  StageType,
  StudentReport,
} from '@/types'
import { removeNullProps } from '@/utils'

import base from './base'

export const gradesApi = base.injectEndpoints({
  overrideExisting: false,
  endpoints: (builder) => ({
    getStages: builder.query<StageType[], string | undefined>({
      query: (classSubId) => `totvs/classroom-subjects/${classSubId}/stages`,
      transformResponse: (res: Res<StageType[]>) => res.data,
      providesTags: ['Evaluations'],
    }),
    getStagesEvaluations: builder.query<AvaliacaoEtapaType[], string | undefined>({
      query: (classSubId) => `totvs/classroom-subjects/${classSubId}/evaluations`,
      transformResponse: (res: Res<AvaliacaoEtapaType[]>) => res.data,
      providesTags: ['Evaluations'],
    }),
    getStudentGrades: builder.query<
      NotaType[],
      { classId?: string; stageCode?: string; evaluationCode?: string }
    >({
      query: ({ classId, stageCode, evaluationCode }) => ({
        url: `totvs/classroom-subjects/${classId}/grades`,
        params: { stage_code: stageCode, evaluation_code: evaluationCode },
      }),
      transformResponse: (res: Res<NotaType[]>) => res.data,
      providesTags: ['Grades'],
    }),
    getStudentsReports: builder.query<StudentReport[], string | undefined>({
      query: (classId) => `totvs/classroom-subjects/${classId}/grades/report`,
      transformResponse: (res: Res<StudentReport[]>) => res.data,
      providesTags: ['Grades', 'Evaluations'],
    }),
    addStudentGrade: builder.mutation<
      void,
      { classSubId: string | undefined; body: { grades: NotaType[] | undefined } }
    >({
      query: ({ classSubId, body }) => ({
        url: `totvs/classroom-subjects/${classSubId}/grades`,
        method: 'POST',
        body: { grades: body.grades?.map((grade) => removeNullProps(grade)) },
      }),
      invalidatesTags: ['Grades'],
    }),
    addStageEvaluation: builder.mutation<
      void,
      { id: string | undefined; body: AddStageType }
    >({
      query: ({ id, body }) => ({
        url: `totvs/classroom-subjects/${id}/evaluations`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Evaluations'],
    }),
    updateEvaluation: builder.mutation<
      void,
      { id: string | undefined; evaluation_code: string; body: AddStageType }
    >({
      query: ({ id, evaluation_code, body }) => ({
        url: `totvs/classroom-subjects/${id}/evaluations/${evaluation_code}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Evaluations', 'Grades'],
    }),
    deleteEvaluation: builder.mutation<
      void,
      {
        classSubId: string | undefined
        evaluation_code: string
        stage_code: string
      }
    >({
      query: ({ classSubId, evaluation_code, stage_code }) => ({
        url: `totvs/classroom-subjects/${classSubId}/evaluations/${evaluation_code}/?stage_code=${stage_code}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Evaluations'],
    }),
    extendGrades: builder.mutation<{ message: string }, GradeJustifyPayload>({
      query: ({ id, ...body }) => ({
        url: `totvs/classroom-subjects/${id}/stages/justifies`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_, error) => (!error ? ['Evaluations'] : []),
    }),

    newGetStudentGrades: builder.query<NewStudentGrade[], NewStudentGradeParams>({
      query: ({ id, ...params }) => ({
        url: `teachers/classroom-subjects/${id}/grades`,
        params,
      }),
      transformResponse: (res: Res<NewStudentGrade[]>) => res.data,
      providesTags: ['NewGrades'],
    }),

    newAddStudentGrade: builder.mutation<void, NewAddStudentGrade>({
      query: ({ id, ...body }) => ({
        url: `teachers/classroom-subjects/${id}/grades`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['NewGrades'],
    }),

    newRemoveGrades: builder.mutation<void, NewRemoveStudentGrade>({
      query: ({ id, ...body }) => ({
        url: `teachers/classroom-subjects/${id}/grades`,
        method: 'DELETE',
        body,
      }),
      invalidatesTags: ['NewGrades'],
    }),
  }),
})

export const {
  useGetStagesQuery,
  useExtendGradesMutation,
  useGetStagesEvaluationsQuery,
  useGetStudentGradesQuery,
  useGetStudentsReportsQuery,
  useAddStudentGradeMutation,
  useAddStageEvaluationMutation,
  useUpdateEvaluationMutation,
  useDeleteEvaluationMutation,
  useNewGetStudentGradesQuery,
  useLazyNewGetStudentGradesQuery,
  useNewAddStudentGradeMutation,
  useNewRemoveGradesMutation,
} = gradesApi
